import createFetchClient, { Middleware } from 'openapi-fetch';
import createClient from 'openapi-react-query';
import { paths } from '@src/types/schema';
import { Environment } from '@src/environment';
import { getValidAccessToken, renewSession } from '@src/auth/tokenRefresh';

export const getAuthHeader = (accessToken: string | undefined) => ({
  Authorization: accessToken ? `Bearer ${accessToken}` : '',
});

// Shape matches NestJS's exception filter so queryClient's throwOnError sees the
// same `statusCode` it would get from a real backend 401.
const unauthorizedResponse = (): Response =>
  new Response(JSON.stringify({ message: 'Unauthorized', statusCode: 401 }), {
    status: 401,
    statusText: 'Unauthorized',
    headers: { 'Content-Type': 'application/json' },
  });

// Sending a request consumes its body stream, so the request handed to
// onResponse can no longer be used to build the retry: `new Request()` from a
// used request throws. Keep an unconsumed clone, keyed by the request object
// openapi-fetch passes through both hooks, so a retry can resend the body.
const originalRequests = new WeakMap<Request, Request>();

const authMiddleware: Middleware = {
  async onRequest({ request }) {
    // Renew before sending rather than after a rejection. The previous version
    // called startSilentRenew() here, which only arms the accessTokenExpiring
    // timer and never actually refreshes, so requests went out with a dead token
    // whenever that timer had not fired.
    const accessToken = await getValidAccessToken();
    if (!accessToken) {
      // No usable session and renewal failed. Skip the network round trip and
      // let the normal 401 path drive the redirect to login.
      return unauthorizedResponse();
    }
    request.headers.set(
      'Authorization',
      getAuthHeader(accessToken).Authorization,
    );
    originalRequests.set(request, request.clone());
    return request;
  },

  async onResponse({ response, request }) {
    if (response.status !== 401) {
      return response;
    }
    // The token was accepted as fresh above, so a 401 here means the server
    // rejected it anyway (clock skew, session revoked server-side). Renew once
    // and replay before giving up.
    try {
      const renewed = await renewSession();
      const accessToken = renewed?.access_token;
      if (!accessToken) {
        return response;
      }
      const original = originalRequests.get(request) ?? request;
      const headers = new Headers(original.headers);
      headers.set('Authorization', getAuthHeader(accessToken).Authorization);
      return await fetch(new Request(original, { headers }));
    } catch {
      return response;
    }
  },
};

export const fetchClient = createFetchClient<paths>({
  baseUrl: Environment.BACKEND_URL,
});
fetchClient.use(authMiddleware);
export const apiClient = createClient(fetchClient);

// A client without the auth middleware, for endpoints that must work before the
// user logs in (e.g. the announcement bar on the login/registration page). The
// auth middleware short-circuits unauthenticated requests with a synthetic 401,
// which would otherwise break pre-login public calls.
export const publicFetchClient = createFetchClient<paths>({
  baseUrl: Environment.BACKEND_URL,
});
export const publicApiClient = createClient(publicFetchClient);
